import React, { useState, useEffect } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import DatePicker from '../components/DatePicker.jsx';
import Message from '../components/Message.jsx';
import PermissionGuard from '../components/PermissionGuard.jsx';
import { MODULES, ACTIONS } from '../context/PermissionsContext.jsx';

function HolidayMaster() {
  const { state, loadHolidays } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    holidayName: '',
    fromDate: '',
    toDate: '',
    createEveryYear: false
  });
  
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate dates
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    
    if (toDate < fromDate) {
      setMessage({ text: 'To date should be same or after from date!', type: 'error' });
      return;
    }
    
    // Check for duplicate holiday name
    const existingHoliday = (state.holidays || []).find(holiday => 
      holiday.holidayName.toLowerCase() === formData.holidayName.toLowerCase() &&
      (!editingHoliday || holiday.id !== editingHoliday.id)
    );
    
    if (existingHoliday) {
      setMessage({ text: 'Holiday name already exists!', type: 'error' });
      return;
    }
    // 🚨 Prevent duplicate / overlapping holiday dates
const isDateConflict = (state.holidays || []).some(holiday => {
  if (editingHoliday && holiday.id === editingHoliday.id) {
    return false; // allow editing same record
  }

  const existingFrom = new Date(holiday.fromDate);
  const existingTo = new Date(holiday.toDate);

  // Check overlap
  return (
    fromDate <= existingTo &&
    toDate >= existingFrom
  );
});

if (isDateConflict) {
  setMessage({
    text: "A holiday already exists for the selected date(s)!",
    type: "error"
  });
  return;
}
    try {
      if (editingHoliday) {
        await api.holidays.update(editingHoliday.id, formData);
        setMessage({ text: 'Holiday updated successfully!', type: 'success' });
      } else {
        await api.holidays.create(formData);
        setMessage({ text: 'Holiday created successfully!', type: 'success' });
      }
      loadHolidays();
      resetForm();
    } catch (error) {
      console.error('Error saving holiday:', error);
      setMessage({ text: 'Error saving holiday!', type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      holidayName: '',
      fromDate: '',
      toDate: '',
      createEveryYear: false
    });
    setShowModal(false);
    setEditingHoliday(null);
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      holidayName: holiday.holidayName,
      fromDate: holiday.fromDate ? new Date(holiday.fromDate).toISOString().split('T')[0] : '',
      toDate: holiday.toDate ? new Date(holiday.toDate).toISOString().split('T')[0] : '',
      createEveryYear: holiday.createEveryYear
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.holidays.delete(id);
        loadHolidays();
        setMessage({ text: 'Holiday deleted successfully!', type: 'success' });
      } catch (error) {
        console.error('Error deleting holiday:', error);
        setMessage({ text: 'Error deleting holiday!', type: 'error' });
      }
    }
  };

  const calculateDays = (fromDate, toDate) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // Load holidays when component mounts
  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Handle browser back button to close modal
  useEffect(() => {
    const handlePopState = (event) => {
      if (showModal) {
        event.preventDefault();
        event.stopPropagation();
        resetForm();
      }
    };

    if (showModal) {
      window.history.pushState({ page: 'holidays', modalOpen: true }, '', '#holidays-modal');
      window.addEventListener('popstate', handlePopState, true);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [showModal]);

  return (
    <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.VIEW} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
      <div className="holiday-master">
      {message.text && (
        <Message 
          message={message.text} 
          type={message.type} 
          onClose={() => setMessage({ text: '', type: '' })} 
        />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Holiday Master</h2>
        <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.CREATE} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
          <button className="btn btn-outline-primary" onClick={() => {
            setShowModal(true);
          }}>
            Add Holiday
          </button>
        </PermissionGuard>
      </div>

      {showModal && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.3)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-sm border-0">
              <div className="modal-header border-bottom" style={{backgroundColor: 'var(--primary-light)'}}>
                <h6 className="modal-title" style={{color: 'var(--primary-color)'}}>{editingHoliday ? 'Edit' : 'Add'} Holiday</h6>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-3">
                  <div className="row g-2">
                    <div className="col-12">
                      <label className="form-label small text-muted">Holiday Name *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.holidayName}
                        onChange={(e) => setFormData({...formData, holidayName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">From Date *</label>
                      <DatePicker
                        value={formData.fromDate}
                        onChange={(date) => setFormData({...formData, fromDate: date})}
                        placeholder="Select start date"
                        className="form-control form-control-sm"
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">To Date *</label>
                      <DatePicker
                        value={formData.toDate}
                        onChange={(date) => setFormData({...formData, toDate: date})}
                        placeholder="Select end date"
                        className="form-control form-control-sm"
                        minDate={formData.fromDate}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-check-sm">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.createEveryYear}
                          onChange={(e) => setFormData({...formData, createEveryYear: e.target.checked})}
                        />
                        <label className="form-check-label small text-muted">
                          Create this Holiday for every year
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light py-2">
                  <button type="submit" className="btn btn-sm btn-primary">
                    Save
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => {
                    resetForm();
                  }}>
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Holiday Name</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Days</th>
                  <th>Every Year</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(state.holidays || []).map(holiday => (
                  <tr key={holiday.id}>
                    <td>{holiday.holidayName}</td>
                    <td>{holiday.fromDate}</td>
                    <td>{holiday.toDate}</td>
                    <td>{calculateDays(holiday.fromDate, holiday.toDate)}</td>
                    <td>{holiday.createEveryYear ? 'Yes' : 'No'}</td>
                    <td>
                      <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.EDIT} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
                        <button
                          title='Edit'
                        className="btn btn-sm btn-primary me-1" 
                        onClick={() => handleEdit(holiday)} style={{ fontSize: '13px', padding: '4px 10px' }}>
                        <i className="bi bi-pencil-square"></i>
                        </button>
                      </PermissionGuard>
                      <PermissionGuard module={MODULES.HOLIDAYS} action={ACTIONS.DELETE} submodule={MODULES.HOLIDAYS.submodules.HOLIDAY_MASTER}>
                        <button
                          title='Delete'
                          className="btn btn-sm btn-danger" 
                          onClick={() => handleDelete(holiday.id)} style={{ fontSize: '13px', padding: '4px 10px' }}>
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </PermissionGuard>
  );
}

export default HolidayMaster;